import { createActor } from "xstate";
import type { ActorRefFrom } from "xstate";
import type { Story } from "inkjs";
import { narrativeMachine } from "../../machines/narrativeMachine.js";
import { loadInkStory, stepStory } from "../loaders/InkLoader.js";
import { EventBus } from "../EventBus.js";
import { SessionStore } from "../../store/SessionStore.js";
import { WorldStore } from "../../store/WorldStore.js";
import { bindContractToStory, INK_OBSERVED_VARS } from "./InkContract.js";
import { routeContractVariable } from "./ContractVariableRouter.js";
import { randomInt } from "../combat/dice.js";
import type { DungeonMap, Position } from "../types/grid.js";
import { PartyStore } from "../../store/PartyStore.js";
import { pushFeed, formatSkillCheck } from "../Feed.js";
import { rollSkillCheck } from "../combat/skillCheck.js";
import { SKILL_NAMES, type Character, type SkillName } from "../types/entity.js";
import { setSkillCheckResolver } from "./InkContract.js";
import { inkUrl } from "../adventure/ActiveAdventure.js";

/**
 * Chebyshev radius used by N02 proximity-bark scanner. 2 cells ≈ "a square
 * or two away" — close enough that a bark feels reactive, far enough that
 * it doesn't require the player to be on top of the NPC.
 */
export const BARK_RADIUS_CELLS = 2;

// ── NarrativeManager ─────────────────────────────────────────────────────────

/**
 * Orchestrates the Ink runtime and XState narrativeMachine.
 *
 * Responsibilities:
 *  - Loads the compiled Ink story JSON for the current map.
 *  - Drives `narrativeMachine` by feeding it STEP_READY events after each
 *    machine transition to the `dialogue` state.
 *  - Detects narrative triggers when the player moves.
 *  - Mirrors Ink variable changes to `SessionStore` and `EventBus`.
 *  - Exposes `narrativeActor` for React components to subscribe to.
 */
export class NarrativeManager {
  /** Public so React hooks can call useSelector on it. */
  readonly narrativeActor: ActorRefFrom<typeof narrativeMachine>;

  private story: Story | null = null;
  private currentMapId: string | null = null;
  private flavorLines: Record<string, readonly string[]> = {};
  /**
   * Per-map observed variable names registered dynamically by the world
   * layer (e.g. `light_<id>_off` from W22 ceiling lights). `loadStoryForMap`
   * clears this set; `registerObservedVariable` re-adds and, if the story
   * is already loaded, binds the observer immediately. Dispatched alongside
   * the static `INK_OBSERVED_VARS` every time a story loads or reloads.
   */
  private dynamicObservedVars: Set<string> = new Set();

  constructor() {
    this.narrativeActor = createActor(narrativeMachine).start();

    // Every time the machine enters the `dialogue` state, pull the next
    // Ink step and feed it back as STEP_READY.
    this.narrativeActor.subscribe((snapshot) => {
      if (snapshot.matches("dialogue") && this.story) {
        this.advanceStep();
      }
    });

    // W08: award XP when an Ink knot calls give_xp(amount).
    EventBus.on("world:give_xp", ({ amount }) => {
      PartyStore.awardXp(amount);
      EventBus.emit("ui:party_update", {});
    });

    // N04: wire the remaining Ink external stubs to real engine behaviour.
    // `roll_skill` is wired via setSkillCheckResolver (below) because Ink
    // needs a synchronous return; the others are fire-and-forget feed lines
    // or handoffs that deeper systems (shop UI, faction flip) will extend.
    EventBus.on("narrative:bark", ({ line }) => {
      pushFeed(line);
    });
    EventBus.on("inventory:give_item", ({ itemId, qty }) => {
      const word = qty === 1 ? "a" : String(qty);
      pushFeed(`You receive ${word} ${itemId}.`);
    });
    EventBus.on("narrative:open_shop", ({ npcId }) => {
      pushFeed(`${npcId} opens their wares for trade.`);
    });
    EventBus.on("enemy:set_hostile", ({ npcId }) => {
      pushFeed(`${npcId} turns hostile.`);
    });

    // W22: push world-side light toggles back into Ink so authors can gate
    // dialogue on `light_<id>_off`. No-op when the var isn't declared in
    // the active story or no story is loaded.
    EventBus.on("world:light_toggled", ({ lightId, off }) => {
      this.setInkVariable(`light_${lightId}_off`, off);
    });

    setSkillCheckResolver((skillRaw, dc) => this.resolveInkSkillCheck(skillRaw, dc));
  }

  // ── Story loading ──────────────────────────────────────────────────────────

  /**
   * Load (and cache) the Ink story for `mapId` from the active adventure's
   * ink root (defaults to `/bkcrawl-mvp/ink/<mapId>.ink.json`).
   * Silently no-ops if the file is absent — triggers will just not fire.
   */
  async loadStoryForMap(mapId: string): Promise<void> {
    this.story = null;
    this.currentMapId = mapId;
    this.dynamicObservedVars.clear();
    await this.loadFlavorLines();
    try {
      const story = await loadInkStory(inkUrl(mapId));
      bindContractToStory(story);
      this.story = story;
      for (const varName of INK_OBSERVED_VARS) {
        this.observeVariable(varName);
      }
      for (const varName of this.dynamicObservedVars) {
        this.observeVariable(varName);
      }
      // EV-E10: auto-observe every `quest_*` global the story declares, so
      // writers add quest flags without an engineering registration step.
      const globals = (
        story.variablesState as unknown as { _globalVariables?: Map<string, unknown> }
      )._globalVariables;
      if (globals) {
        for (const varName of globals.keys()) {
          if (varName.startsWith("quest_")) this.observeVariable(varName);
        }
      }
    } catch {
      // No story file for this map — triggers silently no-op.
      console.info(`[NarrativeManager] No ink story for map "${mapId}" — narrative disabled.`);
    }
  }

  /**
   * Load the flavor-line bank from `/engine/flavor_lines.json`.
   * Already-loaded data is preserved — subsequent calls are no-ops.
   * Silently skips if the file is missing (e.g. test environments).
   */
  async loadFlavorLines(): Promise<void> {
    if (Object.keys(this.flavorLines).length > 0) return;
    try {
      const res = await fetch("/engine/flavor_lines.json");
      if (!res.ok) return;
      this.flavorLines = (await res.json()) as Record<string, readonly string[]>;
    } catch {
      // Non-fatal — game runs fine without flavor lines.
    }
  }

  /**
   * Draw a random line from `category` in the flavor-line bank.
   * Returns null if the category is empty or the bank hasn't been loaded.
   * Uses the seeded RNG so results are deterministic in tests.
   */
  drawFlavorLine(category: string): string | null {
    const lines = this.flavorLines[category];
    if (!lines || lines.length === 0) return null;
    return lines[randomInt(0, lines.length - 1)] ?? null;
  }

  /**
   * Register an Ink variable observer so world-affecting variables (e.g.
   * `has_key`, `boss_dead`) get mirrored to SessionStore automatically.
   *
   * Call this AFTER loadStoryForMap(). Content team must declare the
   * variable with `VAR` in their Ink script. See INK_GUIDE.md.
   */
  /**
   * Register a per-map observed variable (W22). Safe to call before
   * `loadStoryForMap` completes — the name is queued and bound when the
   * story finishes loading. If a story is already loaded, binds now.
   */
  registerObservedVariable(variableName: string): void {
    this.dynamicObservedVars.add(variableName);
    if (this.story) this.observeVariable(variableName);
  }

  /**
   * Write to an Ink story variable, matching the author's declared `VAR`.
   * Silently skipped when no story is loaded or the variable is undeclared
   * — lets world-side systems (W22 lights) push state without requiring
   * every adventure Ink file to declare every optional flag.
   */
  setInkVariable(variableName: string, value: unknown): void {
    if (!this.story) return;
    try {
      this.story.variablesState[variableName] = value;
    } catch {
      // Variable not declared — skip.
    }
  }

  observeVariable(variableName: string): void {
    if (!this.story) return;
    try {
      this.story.ObserveVariable(variableName, (name: string, value: unknown) => {
        SessionStore.setInkVariable(name, value);
        EventBus.emit("narrative:variable_changed", { variableName: name, value });
        routeContractVariable(name, value);
      });
    } catch {
      // Variable is not declared in this story — skip silently.
    }
  }

  // ── Narrative control ──────────────────────────────────────────────────────

  /**
   * Jump to a named Ink knot and begin the dialogue flow.
   * No-op if no story is loaded, the named knot does not exist,
   * or a dialogue is already active.
   */
  startKnot(knotName: string, portrait?: string, npcInstanceId?: string): void {
    if (!this.story) return;
    if (!this.narrativeActor.getSnapshot().matches("inactive")) return;

    try {
      this.story.ChoosePathString(knotName);
    } catch {
      console.warn(`[NarrativeManager] Ink knot "${knotName}" not found in story.`);
      return;
    }

    // The persistent party voice answers every knot — ambient triggers,
    // terminals, and NPC dialogue alike. `getVoice()` falls back to the
    // first living member, so multi-member parties never see the picker;
    // the DialogueBox portrait row survives only as the no-voice fallback.
    const speakerId = PartyStore.getVoice()?.instanceId ?? undefined;

    this.narrativeActor.send({
      type: "START",
      inkKnot: knotName,
      ...(portrait !== undefined ? { portrait } : {}),
      ...(speakerId !== undefined ? { speakerId } : {}),
    });
    EventBus.emit("narrative:start", {
      inkKnot: knotName,
      ...(npcInstanceId !== undefined ? { speakerInstanceId: npcInstanceId } : {}),
    });
  }

  /**
   * Make an Ink choice by index and advance the dialogue.
   * The narrativeMachine will transition dialogue → displayingText automatically
   * via the subscription that fires when it re-enters `dialogue`.
   */
  selectChoice(index: number): void {
    if (!this.story) return;
    this.story.ChooseChoiceIndex(index);
    this.narrativeActor.send({ type: "CHOICE_SELECTED", index });
  }

  /** Close/dismiss the active dialogue. */
  close(): void {
    this.narrativeActor.send({ type: "CLOSE" });
    EventBus.emit("narrative:ended", {});
  }

  /** True while any dialogue is showing (use to gate player movement). */
  isActive(): boolean {
    return !this.narrativeActor.getSnapshot().matches("inactive");
  }

  // ── Trigger detection ──────────────────────────────────────────────────────

  /**
   * Check whether the player's new position is a one-shot narrative trigger,
   * and fire any in-range NPC proximity barks (N02) for the step.
   * Call from the InputHandler `onMove` callback on every successful move.
   */
  checkPlayerMove(pos: Position, dungeonMap: DungeonMap): void {
    this.checkProximityBarks(pos, dungeonMap);

    const cellIdx = pos.z * dungeonMap.width + pos.x;
    const cell = dungeonMap.cells[cellIdx];
    if (cell?.metadata?.type !== "trigger") return;

    const meta = cell.metadata;
    const inkKnot = meta.ink_knot;
    if (!inkKnot) return;

    const triggerId = String(meta.objectId);
    if (WorldStore.isTriggerFired(triggerId)) return;

    WorldStore.markTriggerFired(triggerId);
    EventBus.emit("world:trigger_fired", { triggerId });
    // Also announce under the knot name so spawners (and any other
    // trigger consumer) can bind to the readable authored id instead of
    // Tiled's numeric objectId. Same one-shot guard covers both.
    if (inkKnot !== triggerId) {
      EventBus.emit("world:trigger_fired", { triggerId: inkKnot });
    }
    this.startKnot(inkKnot);
  }

  /**
   * Scan cells within `BARK_RADIUS_CELLS` of `pos` (Chebyshev distance) for
   * NPC metadata carrying a `bark` string. Push each new bark into the feed
   * and record it in `WorldStore.firedBarks` so it doesn't repeat when the
   * player pads around the same NPC (N02).
   *
   * Exposed publicly so tests can drive it without threading a fake
   * InputHandler through the boot path.
   */
  checkProximityBarks(pos: Position, dungeonMap: DungeonMap): void {
    const r = BARK_RADIUS_CELLS;
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        const x = pos.x + dx;
        const z = pos.z + dz;
        if (x < 0 || x >= dungeonMap.width || z < 0 || z >= dungeonMap.height) continue;
        const cell = dungeonMap.cells[z * dungeonMap.width + x];
        const meta = cell?.metadata;
        if (meta?.type !== "npc" || !meta.bark) continue;
        const npcId = String(meta.objectId);
        if (WorldStore.isBarkFired(npcId)) continue;
        WorldStore.markBarkFired(npcId);
        pushFeed(meta.bark);
      }
    }
  }

  // ── N04: skill-check resolver ──────────────────────────────────────────────

  /**
   * Resolve `roll_skill(skill, dc)` called from Ink.
   *
   * Reads the active speaker from the narrativeMachine context, rolls a
   * skill check against their stats, pushes a canonical feed line (N06),
   * and returns the boolean result for Ink to branch on.
   *
   * Degenerate cases (no speaker selected, unknown skill, no character
   * record) fail closed: feed a warning, return `false`.
   */
  resolveInkSkillCheck(rawSkill: string, dc: number): boolean {
    const skill = normalizeSkillName(rawSkill);
    if (!skill) {
      pushFeed(`Skill check skipped — unknown skill "${rawSkill}".`);
      return false;
    }

    const speakerId = this.narrativeActor.getSnapshot().context.speakerId;
    if (!speakerId) {
      pushFeed(`Skill check skipped — no speaker selected.`);
      return false;
    }

    const member = PartyStore.getMember(speakerId);
    if (!member) {
      pushFeed(`Skill check skipped — speaker not found.`);
      return false;
    }

    // Narrow PlayerCharacter → Character when the C01-era proficiency
    // fields are present. The MVP party (hardcoded PlayerCharacter) has
    // none, so we fail closed there.
    if (!("skillProficiencies" in member)) {
      pushFeed(`Skill check skipped — speaker has no skill data.`);
      return false;
    }
    const character = member as Character;
    const result = rollSkillCheck(character, skill, dc);
    EventBus.emit("narrative:skill_check_resolved", {
      speakerName: character.name,
      skill,
      dc,
      roll: result.roll,
      bonus: result.bonus,
      total: result.total,
      success: result.success,
    });
    pushFeed(formatSkillCheck(character.name, skill, dc, result.total, result.success));
    return result.success;
  }

  // ── Persistence (P11) ──────────────────────────────────────────────────────

  /**
   * Serialize the active Ink story's runtime state for inclusion in a save
   * file. Returns `null` when no story is loaded — callers should treat
   * that as "no dialogue to restore" rather than an error.
   */
  getInkState(): string | null {
    if (!this.story) return null;
    return this.story.state.ToJson();
  }

  /**
   * Restore the active Ink story's runtime state from a save blob produced
   * by `getInkState()`. No-op if no story is loaded — load flow should call
   * `loadStoryForMap(pointer.mapId)` first, then hand the blob here.
   *
   * Re-registers the observed-variable callbacks so loaded variable values
   * still mirror to `SessionStore` / `EventBus` on subsequent changes.
   */
  loadInkState(json: string): void {
    if (!this.story) return;
    this.story.state.LoadJson(json);
    for (const varName of INK_OBSERVED_VARS) {
      this.observeVariable(varName);
    }
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  dispose(): void {
    this.story = null;
    this.currentMapId = null;
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private advanceStep(): void {
    if (!this.story) return;
    const step = stepStory(this.story);
    this.narrativeActor.send({ type: "STEP_READY", step });

    if (step.ended) {
      EventBus.emit("narrative:ended", {});
    }
  }
}

/**
 * Map a freeform Ink skill string onto a canonical `SkillName`. Authors
 * may write `"persuasion"`, `"Persuasion"`, `"sleight_of_hand"`, etc.; we
 * normalise to the Title-Case form used by `SKILL_NAMES`.
 */
function normalizeSkillName(raw: string): SkillName | null {
  const cleaned = raw.replace(/[_-]+/g, " ").trim().toLowerCase();
  for (const name of SKILL_NAMES) {
    if (name.toLowerCase() === cleaned) return name;
  }
  return null;
}

/** Singleton narrative manager — imported by both engine and React components. */
export const narrativeManager = new NarrativeManager();
